/**
 * Analytics utilities for the PlaceOrder flow.
 * Uses correct pricing via getAnalyticsPricing and filters bundled addons.
 */
/* eslint-disable @typescript-eslint/naming-convention */
import {
  AnalyticsItemCategory,
  CURRENCY_CODE_EUR,
  EcommerceEventTypeV4,
  pushToDataLayer,
} from '../../../common/analytics';
import { MobileM2MAddonCode } from '../../../common/enums';
import { getMobileBroadbandPricing, getVoiceSMEPricing } from '../../../common/utils/addOnUtils';
import {
  DEFAULT_ITEM_BRAND,
  calculateAnalyticsTax,
  getAddOnYearlyPrice,
  getBundleId,
  getCommercialProductToUse,
  getCommercialProductYearlyPrice,
  getCumulativePrice,
  getEcommerceItemsFromAddedAddonOns,
  getItemCategory,
  getLaitenettiCardByMainHeader,
  getProductItemVariant,
  getSubscriptionCardByOffer,
} from '../../../common/utils/analyticsUtils';
import { getManufacturer, physicalProducts } from '../../../common/utils/cartProductUtils';
import { isLaitenettiOffer } from '../../../common/utils/subscriptionUtils';
import { CommercialProductType } from '../../../generated/api/commercialProductType';
import { OnlineModelCategory } from '../../../generated/api/models';
import {
  PaymentTypeEnum,
  createEcommerceItemFromCommercialProducts,
} from '../../../selfservice/common/googleEcommerceEvent';
import { getLaitenettiCardContent } from '../../OrderSubscriptionSelection/content/LaitenettiSubscriptionCardContent';
import { getBundledOfferAddons, isBundledOfferAddon } from '../../ShoppingBasket/shoppingBasketHelpers';
import {
  getAddonPriceCharges,
  getAddonsDisplayData,
  getEffectivePriceForSubscription,
  getOnlineModelForBasketItem,
  isSalesProductModel,
} from '../../ShoppingBasket/shoppingBasketUtils';
import type {
  EcommerceAddToCartEventV4,
  EcommerceItem,
  EcommerceRemoveFromCartEventV4,
  EcommerceSelectItemEventV4,
  EcommerceViewItemListEventV4,
} from '../../../common/analytics';
import type { AddedAddon } from '../../../common/types/addon';
import type { ConfiguredCommercialProduct } from '../../../common/types/commercialProduct';
import type { BasketItem } from '../../../common/types/shoppingBasket';
import type { CampaignAssociation } from '../../../generated/api/campaignAssociation';
import type { CommercialProduct } from '../../../generated/api/commercialProduct';
import type { Offer } from '../../../generated/api/offer';
import type { OnlineModel } from '../../../generated/api/onlineModel';
import type {
  AnalyticsPrices,
  SubCardWithAnalyticsV4Props,
} from '../../HorizontalSubscriptionCard/subscriptionCardsAnalyticsUtils';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/** Gets correct pricing for analytics - uses MBB pricing for Yritysdata, Voice pricing for others. */
const getAnalyticsPricing = (
  cp: CommercialProduct,
  onlineModel: OnlineModel
): { oneTimeCharge?: number; monthlyRecurringCharge?: number } => {
  const isYritysdata = cp.commercialProductName?.includes('Yritysdata');
  if (isYritysdata) {
    return getMobileBroadbandPricing(cp, onlineModel);
  }
  return getVoiceSMEPricing(cp);
};

const mobileM2MAddonCodes = Object.values(MobileM2MAddonCode) as string[];

/** Finds Laitenetti addon code from an array of addon codes. */
const findLaitenettiAddonCode = (codes: string[]): string | undefined =>
  codes.find(code => mobileM2MAddonCodes.includes(code));

/** Extracts unique addon codes from basket item. */
const getSelectedAddOnCodes = (basketItem?: BasketItem): string[] => {
  if (!basketItem?.offer.addOns?.length) {
    return [];
  }
  const codes = basketItem.offer.addOns.flatMap(addon => addon.addOnCodes ?? []);
  return [...new Set(codes)];
};

/**
 * Creates EcommerceItem(s) with correct pricing, card headers, and addon handling.
 * Returns array: main subscription item + separate items for paid addons.
 * Bundled/free addons are included in item_variant but NOT as separate items.
 */
export const createAnalyticsItemsFromOffer = (
  offer: Offer,
  onlineModel: OnlineModel,
  basketItem?: BasketItem,
  quantity = 1,
  voucher?: string
): EcommerceItem[] => {
  const commercialProduct = offer.commercialProducts.find(
    cp =>
      cp.commercialProductCode ===
      (basketItem?.offer.commercialProductGuid ?? offer.commercialProducts[0]?.commercialProductCode)
  );

  if (!commercialProduct) {
    return [];
  }

  const category =
    onlineModel.category === OnlineModelCategory.SELF_SERVICE
      ? getItemCategory(onlineModel.onlineModelName, commercialProduct.productType, commercialProduct.productSubType)
      : (onlineModel.category ?? commercialProduct.productSubType ?? commercialProduct.productType);

  let itemName = commercialProduct.commercialProductName;
  let variant = itemName;

  // Get all displayable addons
  const selectedAddOnCodes = getSelectedAddOnCodes(basketItem);
  const displayableAddons = selectedAddOnCodes.length
    ? getAddonsDisplayData(selectedAddOnCodes, onlineModel, basketItem!)
    : [];

  // Split addons into bundled (free/included) and paid
  const bundledAddons = displayableAddons.filter(addon => isBundledOfferAddon(addon.id, commercialProduct));
  const paidAddons = displayableAddons.filter(addon => !isBundledOfferAddon(addon.id, commercialProduct));

  const itemCategory = category as AnalyticsItemCategory;
  const subscriptionCard =
    onlineModel.category === OnlineModelCategory.SELF_SERVICE
      ? getSubscriptionCardByOffer(offer, commercialProduct, itemCategory)
      : undefined;

  if (onlineModel.category === OnlineModelCategory.SELF_SERVICE) {
    if (subscriptionCard) {
      itemName = subscriptionCard.mainHeader;
      variant = getProductItemVariant(itemCategory, offer, subscriptionCard);
    } else {
      // Fallback: use getProductItemVariant without subscription card
      variant = getProductItemVariant(itemCategory, offer);
    }
  } else {
    // For devices (PHONE, COMPUTERS, etc.), use onlineModel properties to match view_item_list
    itemName = onlineModel.onlineModelName;
    variant = commercialProduct.commercialProductName;
  }

  // Only include BUNDLED addon names in item_variant (not paid addons)
  if (bundledAddons.length > 0) {
    const bundledAddonNames = bundledAddons.map(addon => addon.name);
    variant = `${variant} ${bundledAddonNames.join(' ')}`;
  }

  // Apply correct pricing for SELF_SERVICE products
  let cpForAnalytics = commercialProduct;
  if (onlineModel.category === OnlineModelCategory.SELF_SERVICE) {
    if (basketItem) {
      // getEffectivePriceForSubscription handles Yritysdata, Voice, and M2M internally
      const effectivePricing = getEffectivePriceForSubscription(basketItem, onlineModel).price;
      cpForAnalytics = { ...commercialProduct, ...effectivePricing };
    } else {
      // No basket context - use simple pricing helper
      cpForAnalytics = { ...commercialProduct, ...getAnalyticsPricing(commercialProduct, onlineModel) };
    }
  }

  // Only add BUNDLED addon prices to subscription price (not paid addon prices)
  if (bundledAddons.length > 0) {
    let addonMonthlyTotal = 0;
    let addonOneTimeTotal = 0;

    bundledAddons.forEach(addon => {
      const modelAddon = onlineModel.addOns?.find(a => a.addOnProductName === addon.name);
      if (modelAddon) {
        const { oneTimeCharge, monthlyCharge } = getAddonPriceCharges(modelAddon, commercialProduct, onlineModel);
        addonMonthlyTotal += (monthlyCharge || 0) * (addon.quantity ?? 1);
        addonOneTimeTotal += (oneTimeCharge || 0) * (addon.quantity ?? 1);
      }
    });

    if (cpForAnalytics.payments === 1) {
      cpForAnalytics = {
        ...cpForAnalytics,
        oneTimeCharge: (cpForAnalytics.oneTimeCharge || 0) + addonOneTimeTotal + addonMonthlyTotal * 12,
      };
    } else {
      cpForAnalytics = {
        ...cpForAnalytics,
        monthlyRecurringCharge: (cpForAnalytics.monthlyRecurringCharge || 0) + addonMonthlyTotal,
        oneTimeCharge: (cpForAnalytics.oneTimeCharge || 0) + addonOneTimeTotal,
      };
    }
  }

  // For SELF_SERVICE subscriptions, always use "Elisa" as brand since getManufacturer doesn't handle this category
  const brand =
    onlineModel.category === OnlineModelCategory.SELF_SERVICE
      ? DEFAULT_ITEM_BRAND
      : getManufacturer(onlineModel, commercialProduct.commercialProductName);

  // Determine item_id: devices use onlineModelCode, Laitenetti uses addon code, others use commercialProductCode
  const laitenettiCode = findLaitenettiAddonCode(selectedAddOnCodes);
  let itemId: string;
  if (onlineModel.category !== OnlineModelCategory.SELF_SERVICE) {
    itemId = onlineModel.onlineModelCode;
  } else if (laitenettiCode) {
    itemId = laitenettiCode;
    const cardsContents = getLaitenettiCardContent(false);
    itemName = cardsContents.find(x => x.selectedAddon === itemId)?.mainHeader ?? itemName;
    variant = `${DEFAULT_ITEM_BRAND} ${itemName}`;
  } else if (subscriptionCard && offer.offerCode !== subscriptionCard.selectedOffer) {
    const defaultOffer = onlineModel.offers.find(o => o.offerCode === subscriptionCard.selectedOffer);
    const defaultCpCode = defaultOffer?.commercialProducts[0]?.commercialProductCode;
    itemId = defaultCpCode ?? commercialProduct.commercialProductCode;
  } else {
    itemId = commercialProduct.commercialProductCode;
  }

  const baseItem = createEcommerceItemFromCommercialProducts(
    itemId,
    category,
    itemName,
    variant,
    [cpForAnalytics],
    quantity,
    brand
  );

  const affiliation =
    basketItem?.source === 'OMAELISA' ? 'Elisa Yrityksille OmaElisa' : 'Elisa Yrityksille Online store';

  const subscriptionItem: EcommerceItem = {
    ...baseItem,
    affiliation,
    ...(voucher && { coupon: voucher }),
  };

  const items: EcommerceItem[] = [subscriptionItem];

  // Add paid addons as separate items using existing getAddOnYearlyPrice
  paidAddons.forEach(displayAddon => {
    const modelAddon = onlineModel.addOns?.find(a => a.addOnCode === displayAddon.id);
    if (modelAddon) {
      const { oneTimeCharge, monthlyCharge } = getAddonPriceCharges(modelAddon, commercialProduct, onlineModel);
      items.push({
        item_brand: DEFAULT_ITEM_BRAND,
        item_category: 'Additional Services',
        item_id: displayAddon.id,
        item_name: displayAddon.name,
        item_variant: displayAddon.name,
        price: getAddOnYearlyPrice({
          addOnCode: displayAddon.id,
          addOnAssociationCode: displayAddon.id,
          displayName: displayAddon.name,
          monthlyPrice: monthlyCharge ?? 0,
          oneTimePrice: oneTimeCharge ?? 0,
        }),
        quantity: displayAddon.quantity ?? 1,
        affiliation,
      });
    }
  });

  return items;
};

/** Gets onlineModel and offer for a basket item. Returns undefined if not found. */
const getBasketItemContext = (
  basketItem: BasketItem,
  onlineModels: OnlineModel[]
): { onlineModel: OnlineModel; offer: Offer } | undefined => {
  const onlineModel = getOnlineModelForBasketItem(onlineModels, basketItem);
  if (!onlineModel) {
    return undefined;
  }

  const offer = onlineModel.offers.find(o => o.offerCode === basketItem.offer.guid);
  if (!offer) {
    return undefined;
  }

  return { onlineModel, offer };
};

// ============================================================================
// PLACEORDER ANALYTICS FUNCTIONS
// ============================================================================

/** Gets EcommerceItem with correct pricing for PlaceOrder flow. */
export const getEcommerceItemFromOfferForPlaceOrder = (
  onlineModelName: string,
  offer: Offer,
  quantity: number,
  onlineModel: OnlineModel,
  analyticsPrices?: AnalyticsPrices,
  index?: number,
  coupon?: string,
  campaignAssociation?: CampaignAssociation,
  itemAnalyticsListName?: string,
  selectedCommercialProducts?: ConfiguredCommercialProduct[],
  addedAddOns?: AddedAddon[]
): EcommerceItem => {
  const commercialProduct = getCommercialProductToUse(offer, selectedCommercialProducts);

  // Devices (phones, tablets, etc.) have hardware-specific categories on the onlineModel
  const isDeviceModel = onlineModel.category != null && (physicalProducts as string[]).includes(onlineModel.category);

  let itemCategory = isDeviceModel
    ? ((onlineModel.category ??
        commercialProduct.productSubType ??
        commercialProduct.productType) as string as AnalyticsItemCategory)
    : getItemCategory(
        onlineModelName,
        commercialProduct.productType,
        commercialProduct.productSubType,
        itemAnalyticsListName
      );

  // For sales products without specific category, use SALES_PRODUCT
  if (
    !isDeviceModel &&
    itemCategory === AnalyticsItemCategory.UNKNOWN_CATEGORY &&
    commercialProduct.productType === CommercialProductType.SALES_PRODUCT
  ) {
    itemCategory = AnalyticsItemCategory.SALES_PRODUCT;
  }

  const isSalesProduct = itemCategory === AnalyticsItemCategory.SALES_PRODUCT;
  const isLaitenetti = itemCategory === AnalyticsItemCategory.LAITE_NETTI;
  const isDeviceOrSalesProduct = isDeviceModel || isSalesProduct;

  const subscriptionCard = isDeviceModel
    ? undefined
    : getSubscriptionCardByOffer(offer, commercialProduct, itemCategory);

  // Apply correct pricing (skip for devices, SALES_PRODUCT, and LAITE_NETTI — they use CP pricing directly)
  const skipPricing = isDeviceOrSalesProduct || isLaitenetti;
  const pricing = skipPricing ? {} : getAnalyticsPricing(commercialProduct, onlineModel);
  const effectiveCP = { ...commercialProduct, ...pricing };

  const itemName = isDeviceOrSalesProduct
    ? onlineModel.onlineModelName
    : isLaitenetti
      ? (commercialProduct.commercialProductName ?? subscriptionCard?.mainHeader)
      : (subscriptionCard?.mainHeader ?? commercialProduct.commercialProductName);

  const getItemId = (): string => {
    if (isDeviceOrSalesProduct) {
      return onlineModel.onlineModelCode;
    }
    if (isLaitenetti) {
      const laitenettiCard = getLaitenettiCardByMainHeader(itemName);
      return laitenettiCard?.selectedAddon ?? commercialProduct.commercialProductCode;
    }
    if (subscriptionCard && offer.offerCode !== subscriptionCard.selectedOffer) {
      const defaultOffer = onlineModel.offers.find(o => o.offerCode === subscriptionCard.selectedOffer);
      const defaultCpCode = defaultOffer?.commercialProducts[0]?.commercialProductCode;
      if (defaultCpCode) {
        return defaultCpCode;
      }
    }
    return commercialProduct.commercialProductCode;
  };
  const itemId = getItemId();

  const itemVariant = isDeviceOrSalesProduct
    ? commercialProduct.commercialProductName
    : getProductItemVariant(itemCategory, offer, subscriptionCard, selectedCommercialProducts);

  // Devices use manufacturer as brand, subscriptions/sales products use "Elisa"
  const brand = isDeviceModel
    ? getManufacturer(onlineModel, commercialProduct.commercialProductName)
    : DEFAULT_ITEM_BRAND;

  // For Laitenetti, use the variant addon price from addedAddOns
  const laitenettiAddon = isLaitenetti
    ? addedAddOns?.find(ao => mobileM2MAddonCodes.includes(ao.addOnCode))
    : undefined;
  const price = laitenettiAddon
    ? getAddOnYearlyPrice(laitenettiAddon)
    : getCommercialProductYearlyPrice([effectiveCP], campaignAssociation, isLaitenettiOffer(offer), analyticsPrices);

  return {
    ...(coupon != null && { coupon }),
    ...(index != null && { index }),
    item_brand: brand,
    item_category: itemCategory,
    item_id: itemId,
    ...(itemAnalyticsListName != null && { item_list_name: itemAnalyticsListName }),
    item_name: itemName,
    item_variant: itemVariant,
    price,
    quantity,
  };
};

/** Gets EcommerceItems with correct pricing and filters bundled addons. */
export const getEcommerceItemsForPlaceOrder = (
  onlineModelName: string,
  offer: Offer,
  quantity: number,
  eventType: EcommerceEventTypeV4,
  onlineModel: OnlineModel,
  addedAddOns?: AddedAddon[],
  coupon?: string,
  campaignAssociation?: CampaignAssociation,
  selectedCommercialProducts?: ConfiguredCommercialProduct[]
): EcommerceItem[] => {
  const commercialProduct = getCommercialProductToUse(offer, selectedCommercialProducts);
  const isSalesProduct = commercialProduct.productType === CommercialProductType.SALES_PRODUCT;
  const allCommercialProducts = [
    ...offer.commercialProducts,
    ...(selectedCommercialProducts?.map(cp => cp.commercialProduct) ?? []),
  ];
  const cpWithMonthlyCharge = allCommercialProducts.find(cp => 'monthlyRecurringCharge' in cp);

  const bundleId = getBundleId(
    isSalesProduct && cpWithMonthlyCharge
      ? cpWithMonthlyCharge.commercialProductCode
      : commercialProduct.commercialProductCode,
    addedAddOns
  );

  const bundledAddonCodes = getBundledOfferAddons(commercialProduct)?.map(a => a.addOnCode) ?? [];
  const filteredAddOns = addedAddOns?.filter(ao => !bundledAddonCodes.includes(ao.addOnCode));

  return [
    {
      ...getEcommerceItemFromOfferForPlaceOrder(
        onlineModelName,
        offer,
        quantity,
        onlineModel,
        undefined,
        undefined,
        coupon,
        campaignAssociation,
        undefined,
        selectedCommercialProducts,
        addedAddOns
      ),
      ...(bundleId != null && { item_bundle_id: bundleId }),
    },
    ...getEcommerceItemsFromAddedAddonOns(quantity, eventType, filteredAddOns, bundleId),
  ];
};

/** add_to_cart event for PlaceOrder flow. */
export const getAddToCartEventForPlaceOrder = (
  onlineModelName: string,
  offer: Offer,
  quantity: number,
  onlineModel: OnlineModel,
  addedAddOns?: AddedAddon[],
  voucher?: string,
  campaignAssociation?: CampaignAssociation
): EcommerceAddToCartEventV4 => ({
  event: EcommerceEventTypeV4.ADD_TO_CART,
  ecommerce: {
    currency: CURRENCY_CODE_EUR,
    items: getEcommerceItemsForPlaceOrder(
      onlineModelName,
      offer,
      quantity,
      EcommerceEventTypeV4.ADD_TO_CART,
      onlineModel,
      addedAddOns,
      voucher,
      campaignAssociation
    ),
  },
});

/** remove_from_cart event for PlaceOrder flow. */
export const getRemoveFromCartEventForPlaceOrder = (
  onlineModelName: string,
  offer: Offer,
  quantity: number,
  onlineModel: OnlineModel,
  addedAddOns?: AddedAddon[],
  voucher?: string,
  campaignAssociation?: CampaignAssociation
): EcommerceRemoveFromCartEventV4 => ({
  event: EcommerceEventTypeV4.REMOVE_FROM_CART,
  ecommerce: {
    currency: CURRENCY_CODE_EUR,
    items: getEcommerceItemsForPlaceOrder(
      onlineModelName,
      offer,
      quantity,
      EcommerceEventTypeV4.REMOVE_FROM_CART,
      onlineModel,
      addedAddOns,
      voucher,
      campaignAssociation
    ),
  },
});

/** view_item_list event for PlaceOrder flow. */
export const getViewItemListEventForPlaceOrder = (
  subscriptions: SubCardWithAnalyticsV4Props[] | EcommerceItem[],
  analyticsListName?: string
): EcommerceViewItemListEventV4 => {
  const isEcommerceItemArray = (subs: SubCardWithAnalyticsV4Props[] | EcommerceItem[]): subs is EcommerceItem[] =>
    !subs.length || (subs[0] as EcommerceItem).item_id != null;

  return {
    event: EcommerceEventTypeV4.VIEW_ITEM_LIST,
    ecommerce: {
      items: isEcommerceItemArray(subscriptions)
        ? subscriptions
        : subscriptions.map((sub, index) =>
            getEcommerceItemFromOfferForPlaceOrder(
              sub.onlineModel.onlineModelName,
              sub.offer,
              1,
              sub.onlineModel,
              sub,
              index,
              sub.voucher,
              sub.campaignAssociation,
              analyticsListName
            )
          ),
    },
  };
};

/** select_item event for PlaceOrder flow. */
export const getSelectItemEventForPlaceOrder = (
  { voucher, campaignAssociation, offer, onlineModel }: SubCardWithAnalyticsV4Props,
  index: number,
  itemAnalyticsListName?: string
): EcommerceSelectItemEventV4 => ({
  event: EcommerceEventTypeV4.SELECT_ITEM,
  ecommerce: {
    items: [
      getEcommerceItemFromOfferForPlaceOrder(
        onlineModel.onlineModelName,
        offer,
        1,
        onlineModel,
        undefined,
        index,
        voucher,
        campaignAssociation,
        itemAnalyticsListName
      ),
    ],
  },
});

/** Sends select_item event for PlaceOrder flow. */
export const sendSelectionEventForPlaceOrder = (
  subscription: SubCardWithAnalyticsV4Props,
  index: number,
  analyticsListName?: string
): void => {
  pushToDataLayer(getSelectItemEventForPlaceOrder(subscription, index, analyticsListName));
};

// ============================================================================
// EVENT HANDLERS (for basket operations)
// ============================================================================

/** Handles add/remove analytics for PlaceOrder basket operations. */
export const handleAddOrRemoveItemForPlaceOrder = (
  basketItem: BasketItem | undefined,
  newQuantity: number,
  onlineModels: OnlineModel[]
): void => {
  if (!basketItem) {
    return;
  }

  const context = getBasketItemContext(basketItem, onlineModels);
  if (!context) {
    return;
  }

  const { onlineModel, offer } = context;
  const delta = newQuantity - basketItem.quantity;
  const quantity = Math.abs(delta);
  const eventType = delta > 0 ? EcommerceEventTypeV4.ADD_TO_CART : EcommerceEventTypeV4.REMOVE_FROM_CART;

  if (isSalesProductModel(onlineModel)) {
    const event =
      delta > 0
        ? getAddToCartEventForPlaceOrder(onlineModel.onlineModelName, offer, quantity, onlineModel)
        : getRemoveFromCartEventForPlaceOrder(onlineModel.onlineModelName, offer, quantity, onlineModel);
    pushToDataLayer(event);
  } else {
    const ecommerceItems = createAnalyticsItemsFromOffer(offer, onlineModel, basketItem, quantity);
    if (ecommerceItems.length > 0) {
      pushToDataLayer({ event: eventType, ecommerce: { currency: CURRENCY_CODE_EUR, items: ecommerceItems } });
    }
  }
};

/** Handles addon change analytics for PlaceOrder flow. */
export const handleAddonChangeForPlaceOrder = (
  basketItem: BasketItem | undefined,
  productIndex: number,
  newAddons: string[],
  onlineModels: OnlineModel[]
): void => {
  if (!basketItem?.offer.addOns?.[productIndex]) {
    return;
  }

  const oldAddons = basketItem.offer.addOns[productIndex].addOnCodes || [];
  const removedAddons = oldAddons.filter(addon => !newAddons.includes(addon));
  if (!removedAddons.length) {
    return;
  }

  const context = getBasketItemContext(basketItem, onlineModels);
  if (!context) {
    return;
  }

  const ecommerceItems = createAnalyticsItemsFromOffer(
    context.offer,
    context.onlineModel,
    basketItem,
    removedAddons.length
  );
  if (ecommerceItems.length > 0) {
    pushToDataLayer({
      event: EcommerceEventTypeV4.REMOVE_ADDITIONAL_SERVICES,
      ecommerce: { currency: CURRENCY_CODE_EUR, items: ecommerceItems },
    });
  }
};

/** Handles purchase analytics for PlaceOrder flow.
 *  Uses createAnalyticsItemsFromOffer — same as add_to_cart/begin_checkout — for consistent pricing. */
export const handleOrderPurchaseForPlaceOrder = (
  basketItems: BasketItem[],
  orderId: string,
  onlineModels: OnlineModel[],
  transactionCoupon?: string,
  paymentType: PaymentTypeEnum = PaymentTypeEnum.INVOICE
): void => {
  const allEcommerceItems: EcommerceItem[] = [];

  for (const basketItem of basketItems) {
    const context = getBasketItemContext(basketItem, onlineModels);
    if (!context) {
      continue;
    }

    const { onlineModel, offer } = context;

    if (isSalesProductModel(onlineModel)) {
      const items = getEcommerceItemsForPlaceOrder(
        onlineModel.onlineModelName,
        offer,
        basketItem.quantity || 1,
        EcommerceEventTypeV4.PURCHASE,
        onlineModel,
        []
      );
      allEcommerceItems.push(...items);
    } else {
      const items = createAnalyticsItemsFromOffer(
        offer,
        onlineModel,
        basketItem,
        basketItem.quantity || 1,
        transactionCoupon
      );
      allEcommerceItems.push(...items);
    }
  }

  if (allEcommerceItems.length > 0) {
    const cumulativePrice = getCumulativePrice(allEcommerceItems);
    const coupon = transactionCoupon ?? allEcommerceItems[0]?.coupon;

    pushToDataLayer({
      event: EcommerceEventTypeV4.PURCHASE,
      payment_type: paymentType,
      ecommerce: {
        transaction_id: orderId,
        currency: CURRENCY_CODE_EUR,
        value: cumulativePrice,
        tax: calculateAnalyticsTax(cumulativePrice),
        items: allEcommerceItems,
        ...(coupon && { coupon }),
        shipping: 0,
      },
    });
  }
};

/* eslint-enable @typescript-eslint/naming-convention */
